import React, { useRef, useMemo } from 'react';

import { useObserver } from 'mobx-react-lite';
import { Select, Modal, Checkbox, ConfirmModal, Tooltip, Collapse } from '@bringg/react-components';
import map from 'lodash/map';
import { useTranslation } from 'react-i18next';
import { EnumDefinitionItem } from '@bringg/types/types/custom_attribute';
import {
	ENABLE_WEBHOOK_AUTH_ADDITIONAL_HEADERS_FF,
	useWebhooksApiStore,
	Webhook,
	webhookTypeTranslationKey
} from '@bringg-frontend/webhooks-api-internal-stores';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';
import { KeyValueTable, useKeyValueDefinitionTableState } from '@bringg-frontend/key-value-table';

import WebhooksEditActions from './webhooks-edit-actions/webhooks-edit-actions';
import WebhooksEditCondition from './webhooks-edit-condition/webhooks-edit-condition';
import WebhooksEditUrls from './webhooks-edit-urls/webhooks-edit-urls';
import WebhooksEditFields from './webhooks-edit-fields/webhooks-edit-fields';

const { Panel } = Collapse;

interface Props {
	webhook: Webhook;
	closeEdit: () => void;
	isNew?: boolean;
}

const WebhooksEdit: React.FC<Props> = ({ webhook, closeEdit, isNew }) => {
	const { t } = useTranslation();
	const { webhooksAppStore, webhookAuthenticationConfigurationStore } = useWebhooksApiStore();
	const enableAuthorizationPerWebhook = useHasFeatureFlag('enable_authorization_per_webhook');
	const enableWebhookAuthAdditionalHeaders = useHasFeatureFlag(ENABLE_WEBHOOK_AUTH_ADDITIONAL_HEADERS_FF);

	const startedAsFlexible = useRef(webhook.isFlexible);
	const isFlexibleCheckboxDisabled =
		startedAsFlexible.current || !webhook.doesSupportFlexibleWebhook() || webhook.isOnlyFlexible();

	const onWebhookKeyChange = key => {
		webhook.setKey(key);
		webhook.setConditionKey(null);
	};

	const webhooksTypes = useMemo(() => {
		return map(webhooksAppStore.applicationConfig, webhookConfig => ({
			id: webhookConfig.key,
			name: t(webhookTypeTranslationKey(webhookConfig.key), { defaultValue: webhookConfig.title })
		}));
	}, [webhooksAppStore.applicationConfig, t]);

	const onSetIsFlexible = isFlexible => {
		if (isFlexible) {
			ConfirmModal({
				title: t('WEBHOOK_APP.FLEXIBLE_CONFIRM_MODAL.ENABLE_FLEXIBLE_TITLE', {
					defaultValue: 'Enable Flexible Webhook?'
				}),
				content: t('WEBHOOK_APP.FLEXIBLE_CONFIRM_MODAL.ENABLE_FLEXIBLE_DESCRIPTION', {
					defaultValue: 'If you enable flexible webhook it will change the format'
				}),
				okText: t('WEBHOOK_APP.FLEXIBLE_CONFIRM_MODAL.ENABLE_FLEXIBLE_BUTTON', { defaultValue: 'Enable' }),
				okType: 'danger',
				cancelText: t('WEBHOOK_APP.FLEXIBLE_CONFIRM_MODAL.CANCEL_ENABLE_BUTTON', { defaultValue: 'Cancel' }),
				onOk() {
					webhook.setIsFlexible(isFlexible);
				}
			});
		} else {
			webhook.setIsFlexible(isFlexible);
		}

		webhook.setConditionKey(null);
	};

	const getEnableFlexibleCheckbox = () => {
		return (
			<div className="set-flexible-webhook">
				<Checkbox
					className="set-flexible-webhook-checkbox"
					checked={webhook.isFlexible}
					disabled={isFlexibleCheckboxDisabled}
					onChange={e => {
						onSetIsFlexible(e.target.checked);
					}}
				/>
				<span className="set-flexible-webhook-text">
					{t('FLEXIBLE_WEBHOOK.SET_IS_FLEXIBLE', { defaultValue: 'Enable Flexible Webhook' })}
				</span>
			</div>
		);
	};

	const headersDefinition = useMemo(
		() =>
			Object.keys(webhook.headers).reduce((acc, key) => {
				acc.push({ value: key, name: webhook.headers[key] });

				return acc;
			}, []),
		// don't add value to the dependency array it will cause infinite onChange callback invocation
		[]
	);

	const headersDefinitionState = useKeyValueDefinitionTableState({
		allowEmpty: true,
		keyValueDefinition: headersDefinition,
		onEnumDefinitionChange: (headers: EnumDefinitionItem[]) => {
			const h = headers.reduce((acc: Record<string, string>, cur: EnumDefinitionItem) => {
				acc[cur.value] = cur.name as string;

				return acc;
			}, {});

			webhook.setHeaders(h);
		},
		translations: {
			errors: {
				emptyRowMessage: t('WEBHOOK_AUTHENTICATION_METHOD.MODAL.ERRORS.NO_EMPTY_LIST'),
				noSameValue: t('WEBHOOK_AUTHENTICATION_METHOD.MODAL.ERRORS.NO_SAME_VALUE'),
				valueAndNameNotEmpty: t('WEBHOOK_AUTHENTICATION_METHOD.MODAL.ERRORS.VALUE_AND_NAME_NOT_EMPTY')
			}
		}
	});

	return useObserver(() => {
		const title = isNew
			? t('WEBHOOK_APP.MODAL.ADD_WEBHOOK', { defaultValue: 'Add Webhook' })
			: t('WEBHOOK_APP.MODAL.EDIT_WEBHOOK', { defaultValue: 'Edit Webhook' });

		return (
			<Modal
				title={<h1 className="webhook-modal-title">{title}</h1>}
				closable={false}
				visible
				className="webhook-modal"
				destroyOnClose
				footer={<WebhooksEditActions webhook={webhook} closeEdit={closeEdit} isNew={isNew} />}
			>
				<div className="webhooks-edit-form">
					<div className="webhook-type">
						<Select
							showSearch
							optionFilterProp="children"
							className="select-max-width"
							placeholder={t('WEBHOOK_APP.MODAL.WEBHOOK_NAME_FIELD', { defaultValue: 'Webhook Name' })}
							value={webhook.key}
							onChange={onWebhookKeyChange}
							options={webhooksTypes}
						/>
					</div>
					{enableAuthorizationPerWebhook && (
						<div className="webhooks-method-and-url">
							<Select
								showSearch
								optionFilterProp="children"
								className="select-max-width"
								placeholder={t('WEBHOOK_APP.MODAL.WEBHOOK_METHOD_AND_URL')}
								value={webhook.webhookAuthenticationConfigurationId}
								onChange={webhook.setWebhookAuthenticationConfigurationId}
								options={webhookAuthenticationConfigurationStore.getAll}
								allowClear
							/>
						</div>
					)}
					<WebhooksEditCondition webhook={webhook} />
					<WebhooksEditUrls webhook={webhook} />
					{!webhook.doesSupportFlexibleWebhook() && (
						<Tooltip
							title={t('FLEXIBLE_WEBHOOK.TOOLTIP', {
								defaultValue: "This webhook does't support flexible mode yet"
							})}
							placement="topLeft"
						>
							{getEnableFlexibleCheckbox()}
						</Tooltip>
					)}
					{webhook.doesSupportFlexibleWebhook() && getEnableFlexibleCheckbox()}
					{enableWebhookAuthAdditionalHeaders && (
						<Collapse className="headers" removePadding>
							<Panel header={t('WEBHOOK_AUTHENTICATION_METHOD.MODAL.HEADERS')} key="headers-panel-1">
								<KeyValueTable
									state={headersDefinitionState}
									headers={{
										keyLabel: t('WEBHOOK_AUTHENTICATION_METHOD.MODAL.KEY'),
										valueLabel: t('WEBHOOK_AUTHENTICATION_METHOD.MODAL.VALUE')
									}}
								/>
							</Panel>
						</Collapse>
					)}
					{webhook.isFlexible && webhook.doesSupportFlexibleWebhook() && (
						<WebhooksEditFields webhook={webhook} />
					)}
				</div>
			</Modal>
		);
	});
};

export default WebhooksEdit;
